import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type ConsentCategory = 'necessary' | 'analytics' | 'advertising' | 'affiliate';

interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
  advertising: boolean;
  affiliate: boolean;
  timestamp?: string;
  version?: string;
}

interface CookieConsentContextType {
  preferences: ConsentPreferences;
  hasConsent: (category: ConsentCategory) => boolean;
  updateConsent: (preferences: Partial<ConsentPreferences>) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  showBanner: boolean;
  setShowBanner: (show: boolean) => void;
  showPreferences: boolean;
  setShowPreferences: (show: boolean) => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const CONSENT_KEY = 'cookie-consent';
const CONSENT_VERSION = '1.0';
const CONSENT_EXPIRY_MONTHS = 12;

const defaultPreferences: ConsentPreferences = {
  necessary: true,
  analytics: false,
  advertising: false,
  affiliate: false,
};

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<ConsentPreferences>(defaultPreferences);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    // Load saved consent from localStorage
    const savedConsent = localStorage.getItem(CONSENT_KEY);
    
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent);
        
        // Check if consent is expired (12 months)
        if (parsed.timestamp) {
          const consentDate = new Date(parsed.timestamp);
          const expiryDate = new Date(consentDate);
          expiryDate.setMonth(expiryDate.getMonth() + CONSENT_EXPIRY_MONTHS);
          
          if (new Date() > expiryDate) {
            // Consent expired, show banner again
            setShowBanner(true);
            return;
          }
        }
        
        // Check version match
        if (parsed.version !== CONSENT_VERSION) {
          setShowBanner(true);
          return;
        }
        
        setPreferences(parsed);
      } catch (error) {
        console.error('Error loading consent preferences:', error);
        setShowBanner(true);
      }
    } else {
      // No consent saved, show banner
      setShowBanner(true);
    }
  }, []);

  const saveConsent = (prefs: ConsentPreferences) => {
    const consentData = {
      ...prefs,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    setPreferences(consentData);
  };

  const updateConsent = (newPreferences: Partial<ConsentPreferences>) => {
    const updated = {
      ...preferences,
      ...newPreferences,
      necessary: true, // Always true
    };
    saveConsent(updated);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      advertising: true,
      affiliate: true,
    });
    setShowBanner(false);
    setShowPreferences(false);
  };

  const rejectAll = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      advertising: false,
      affiliate: false,
    });
    setShowBanner(false);
    setShowPreferences(false);
  };

  const hasConsent = (category: ConsentCategory): boolean => {
    return preferences[category] === true;
  };

  return (
    <CookieConsentContext.Provider
      value={{
        preferences,
        hasConsent,
        updateConsent,
        acceptAll,
        rejectAll,
        showBanner,
        setShowBanner,
        showPreferences,
        setShowPreferences,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}
